import { queryOptions, useQuery } from '@tanstack/react-query';
import type { FuseResultMatch } from 'fuse.js';
import { getFriends } from '@/hooks/contacts/use-friends';
import type { Friend } from '@/models/user/friend';
import type { Tag } from '@/models/user/tag';
import { getGroupedTagsByMember } from '../tags/use-tags';
import useFuseSearch from './use-fuse-search';

export const friendsWithTagsQueryOptions = queryOptions<Array<Friend & { Tags: Tag[] }>>({
  queryKey: ['/GetFriendsWithTags'],
  queryFn: async ({ signal }) => {
    const friends = await getFriends(signal);
    const tagsByMember: Record<number, Tag[]> = await getGroupedTagsByMember(signal).catch(() => ({}));

    return Object.values(friends).map((friend) => ({ ...friend, Tags: tagsByMember[friend.FriendID] ?? [] }));
  },
  staleTime: 0,
});

export type FuzzySearchResult = {
  highlightRanges: readonly FuseResultMatch[] | undefined;
  score?: number;
};

export type SearchFriend = Friend & {
  Tags: Tag[];
  FuzzySearch?: FuzzySearchResult;
};

interface Options {
  searchIncludeTags?: boolean;
  initialReturnAll?: boolean;
}

export default function useSearchFriends(
  keyword = '',
  { searchIncludeTags, initialReturnAll }: Options = { searchIncludeTags: false, initialReturnAll: true },
) {
  const { data: friendsWithTags = [], ...query } = useQuery(friendsWithTagsQueryOptions);

  const keys = searchIncludeTags ? ['RemarkName', 'FriendNickName', 'Tags.TagName'] : ['RemarkName', 'FriendNickName'];

  const filteredFriends = useFuseSearch({
    data: friendsWithTags,
    keys,
    keyword,
    initialReturnAll,
    mapResultItem: ({ item, matches, score }) => ({
      ...item,
      FuzzySearch: { highlightRanges: matches, score },
    }),
  });

  return {
    friends: friendsWithTags,
    friendCount: friendsWithTags.length,
    filteredFriends,
    keyword,
    ...query,
  };
}
